import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { toast } from 'sonner';
import { COACH_READINESS_OPTIONS, VENUE_READINESS_OPTIONS, PlayerWithStatusInfo } from '@/types/playerStatus';
import { User, Clock, Save, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { BulkPlayerReadinessButton } from './BulkPlayerReadinessButton';

interface PlayerReadinessManagerProps {
  gameId: string;
  gameData: any;
  userRole: 'coach' | 'team_owner' | 'venue';
  teamId?: number;
  teamSide?: 'home' | 'away';
  teamName?: string;
}

export const PlayerReadinessManager: React.FC<PlayerReadinessManagerProps> = ({
  gameId,
  gameData,
  userRole,
  teamId,
  teamSide,
  teamName
}) => {
  const { user } = useAuthenticatedUser();
  const [players, setPlayers] = useState<PlayerWithStatusInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  
  // Check if we're within 1 hour of game time
  const gameDateTime = new Date(`${gameData?.game_date}T${gameData?.game_time}`);
  const oneHourBefore = new Date(gameDateTime.getTime() - 60 * 60 * 1000);
  const canSetReadiness = new Date() >= oneHourBefore;
  
  // Helper function to check if game is more than an hour away
  const isMoreThanHourAway = (gameData: any) => {
    if (!gameData?.game_date || !gameData?.game_time) return true;
    const gameDateTime = new Date(`${gameData.game_date}T${gameData.game_time}`);
    const oneHourBefore = new Date(gameDateTime.getTime() - 60 * 60 * 1000);
    return new Date() < oneHourBefore;
  };

  useEffect(() => {
    loadPlayers();
  }, [gameId, teamId, teamSide]);

  const loadPlayers = async () => {
    try {
      // Start by getting attendance data to find only attending players
      let attendanceQuery = supabase
        .from('player_attendance_status')
        .select('*')
        .eq('game_id', gameId)
        .eq('attendance_status', 'attending'); // Only get attending players

      // Filter by team if specified
      if (teamSide && userRole !== 'venue') {
        attendanceQuery = attendanceQuery.eq('team_side', teamSide);
      }

      const { data: attendanceData, error: attendanceError } = await attendanceQuery;

      if (attendanceError) {
        console.error('Error loading attendance:', attendanceError);
        return;
      }

      if (!attendanceData || attendanceData.length === 0) {
        setPlayers([]);
        return;
      }

      const playersWithStatus: PlayerWithStatusInfo[] = [];

      for (const attendance of attendanceData) {
        // Get player info based on type
        let playerInfo = null;
        if (attendance.player_type === 'supabase') {
          const { data } = await supabase
            .from('profiles')
            .select('first_name, last_name, username')
            .eq('id', attendance.player_id)
            .maybeSingle();
          playerInfo = data;
        } else {
          const { data } = await supabase
            .from('internal_users')
            .select('username')
            .eq('id', attendance.player_id)
            .maybeSingle();
          playerInfo = data;
        }

        // Get readiness status
        const { data: readinessData } = await supabase
          .from('player_readiness_status')
          .select('*')
          .eq('game_id', gameId)
          .eq('player_id', attendance.player_id)
          .maybeSingle();

        playersWithStatus.push({
          id: attendance.player_id,
          playerType: attendance.player_type as 'supabase' | 'internal',
          firstName: playerInfo?.first_name || undefined,
          lastName: playerInfo?.last_name || undefined,
          username: playerInfo?.username || undefined,
          attendanceStatus: {
            id: attendance.id,
            gameId: attendance.game_id,
            playerId: attendance.player_id,
            playerType: attendance.player_type as 'supabase' | 'internal',
            teamSide: attendance.team_side as 'home' | 'away',
            attendanceStatus: attendance.attendance_status as 'attending' | 'not_attending',
            setBy: attendance.set_by,
            setAt: attendance.set_at,
            createdAt: attendance.created_at,
            updatedAt: attendance.updated_at
          },
          readinessStatus: readinessData ? {
            id: readinessData.id,
            gameId: readinessData.game_id,
            playerId: readinessData.player_id,
            playerType: readinessData.player_type as 'supabase' | 'internal',
            teamSide: readinessData.team_side as 'home' | 'away',
            readinessStatus: readinessData.readiness_status as any,
            setBy: readinessData.set_by,
            setByType: readinessData.set_by_type as 'supabase' | 'internal',
            setByRole: readinessData.set_by_role as 'coach' | 'team_owner' | 'venue',
            overrideLevel: readinessData.override_level,
            notes: readinessData.notes,
            setAt: readinessData.set_at,
            createdAt: readinessData.created_at,
            updatedAt: readinessData.updated_at
          } : undefined,
          teamSide: attendance.team_side as 'home' | 'away'
        });
      }

      setPlayers(playersWithStatus);
    } catch (error) {
      console.error('Error loading players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetReadiness = async (playerId: string, status: string, playerType: 'supabase' | 'internal', teamSide: 'home' | 'away') => {
    if (!user) return;

    setUpdating(playerId);

    try {
      // Optimistic update - immediately update the UI state
      setPlayers(prevPlayers => 
        prevPlayers.map(player => 
          player.id === playerId 
            ? {
                ...player,
                readinessStatus: {
                  id: player.readinessStatus?.id || '',
                  gameId: gameId,
                  playerId: playerId,
                  playerType: playerType,
                  teamSide: teamSide,
                  readinessStatus: status as any,
                  setBy: user.id,
                  setByType: 'supabase' as 'supabase' | 'internal',
                  setByRole: userRole,
                  overrideLevel: userRole === 'venue' ? 2 : 1,
                  notes: notes[playerId] || undefined,
                  setAt: new Date().toISOString(),
                  createdAt: player.readinessStatus?.createdAt || new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }
              }
            : player
        )
      );

      const { data, error } = await supabase.rpc('set_player_readiness_status', {
        p_game_id: gameId,
        p_player_id: playerId,
        p_player_type: playerType,
        p_team_side: teamSide,
        p_readiness_status: status,
        p_set_by: user.id,
        p_set_by_type: 'supabase',
        p_set_by_role: userRole,
        p_notes: notes[playerId] || null
      });

      if (error) {
        console.error('Error setting readiness:', error);
        toast.error("Failed to update player readiness status");
        // Revert optimistic update on error
        await loadPlayers();
        return;
      }

      if (data && typeof data === 'object' && 'success' in data && !(data as any).success) {
        toast.error((data as any).error || "Failed to update player readiness status");
        // Revert optimistic update on error
        await loadPlayers();
        return;
      }

      toast.success("Player readiness status updated successfully");
      
      // Also sync to game_participants table for automated checks
      const participationStatus = status === 'present_ready_to_play' ? 'ready_to_play' : 'confirmed';
      
      const { error: participantError } = await supabase
        .from('game_participants')
        .update({ 
          participation_status: participationStatus
        })
        .eq('game_id', gameId)
        .eq('player_id', playerId);
      
      if (participantError) {
        console.error('Error syncing to game_participants:', participantError);
      }

      // Don't reload players on success - the optimistic update should be sufficient
      // Only reload on error to ensure consistency
    } catch (error) {
      console.error('Error:', error);
      toast.error("Failed to update player readiness status");
      // Revert optimistic update on error
      await loadPlayers();
    } finally {
      setUpdating(null);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'present_ready_to_play':
        return 'default';
      case 'injured':
        return 'destructive';
      case 'no_show':
        return 'secondary';
      case 'disqualified_requirements':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const formatStatusLabel = (status: string) => {
    switch (status) {
      case 'present_ready_to_play':
        return 'Present - Ready to Play';
      case 'injured':
        return 'Injured';
      case 'no_show':
        return 'No Show';
      case 'disqualified_requirements':
        return 'Disqualified (Requirements)';
      default:
        return status;
    }
  };

  const getPlayerName = (player: PlayerWithStatusInfo) => {
    if (player.firstName && player.lastName) {
      return `${player.firstName} ${player.lastName}`;
    }
    if (player.username) {
      return player.playerType === 'internal' ? `@${player.username}` : player.username;
    }
    return 'Unknown Player';
  };

  // All players are now attending since we filter at the query level
  const attendingPlayers = players;

  const readinessOptions = userRole === 'venue' ? VENUE_READINESS_OPTIONS : COACH_READINESS_OPTIONS;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Player Readiness Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading players...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Player Readiness Management
          {userRole === 'venue' && <Badge variant="outline">Venue Override Authority</Badge>}
        </CardTitle>
        <CardDescription>
          {userRole === 'venue' 
            ? "Set and confirm player readiness status. Your selections will override coach and team owner inputs."
            : canSetReadiness 
              ? `Set player readiness status for attending players. Options: Present - Ready to Play, Injured, No Show. Use the bulk action below if all ${attendingPlayers.length} players are ready, otherwise set statuses individually.`
              : "Readiness status can be set 1 hour before game time."
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Workflow Information for Coaches */}
        {userRole !== 'venue' && (
          <Alert>
            <Clock className="h-4 w-4" />
            <AlertTitle>Player Readiness Workflow</AlertTitle>
            <AlertDescription>
              Showing {attendingPlayers.length} attending player{attendingPlayers.length !== 1 ? 's' : ''} for this game.
              {!canSetReadiness && (
                <>
                  <br />You can set readiness status starting at {oneHourBefore.toLocaleString()}.
                  Current time: {new Date().toLocaleString()}
                </>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Bulk Action - Only show for coaches/team owners when they can set readiness and have players */}
        {userRole !== 'venue' && attendingPlayers.length > 0 && teamName && teamSide && teamId && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Quick Actions</Label>
            <BulkPlayerReadinessButton
              gameId={gameId}
              teamId={teamId}
              teamSide={teamSide}
              teamName={teamName}
              gameDateTime={gameData?.game_date && gameData?.game_time ? `${gameData.game_date}T${gameData.game_time}` : undefined}
              disabled={!canSetReadiness}
            />
            <p className="text-xs text-muted-foreground">
              This will mark all attending players without a current status as "Present - Ready to Play". Players already marked as Injured or No Show will be skipped.
            </p>
          </div>
        )}

        {attendingPlayers.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Attending Players</h3>
            <p className="text-muted-foreground">
              No players have confirmed their attendance for this game yet. Players must confirm attendance in the Player Section before they will appear here for readiness management.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <h4 className="text-sm font-semibold">
              Attending Players ({attendingPlayers.length})
            </h4>
            
            {attendingPlayers.map((player) => (
              <div key={player.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium">{getPlayerName(player)}</h5>
                    <p className="text-sm text-muted-foreground">
                      {player.teamSide === 'home' ? 'Home Team' : 'Away Team'}
                    </p>
                  </div>
                  {player.readinessStatus && (
                    <Badge variant={getStatusBadgeVariant(player.readinessStatus.readinessStatus)}>
                      {formatStatusLabel(player.readinessStatus.readinessStatus)}
                    </Badge>
                  )}
                </div>

                {(canSetReadiness || userRole === 'venue') && (
                  <div>
                    <Label>Readiness Status</Label>
                    <Select
                      key={`${player.id}-${player.readinessStatus?.readinessStatus || 'unset'}`}
                      value={player.readinessStatus?.readinessStatus || undefined}
                      onValueChange={(value) => handleSetReadiness(
                        player.id,
                        value,
                        player.playerType,
                        player.teamSide
                      )}
                      disabled={updating === player.id}
                    >
                      <SelectTrigger>
                        <SelectValue 
                          placeholder="Set readiness status"
                          className={player.readinessStatus?.readinessStatus ? 'text-foreground' : 'text-muted-foreground'}
                        />
                      </SelectTrigger>
                      <SelectContent className="bg-background border z-50">
                        {readinessOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {player.readinessStatus && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>
                      Set by: {player.readinessStatus.setByRole} on {new Date(player.readinessStatus.setAt).toLocaleString()}
                    </p>
                    {player.readinessStatus.notes && (
                      <p>Notes: {player.readinessStatus.notes}</p>
                    )}
                  </div>
                )}

                {updating === player.id && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="text-sm">Updating status...</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};